import { useCallback, useMemo } from "react";

import { useLocalStorage } from "@/lib/hooks/useLocalStorage";

/**
 * Parse the persisted value into a key list. Defensive against non-string values: useLocalStorage's
 * deserializer auto-parses JSON-looking strings, so a stale array value (from an earlier format) or
 * hand-edited garbage can come back as a non-string — treat anything unexpected as "nothing hidden"
 * rather than crashing.
 */
const parseHiddenKeys = (raw: unknown): string[] =>
  typeof raw === "string" && raw.length > 0 ? raw.split(",") : [];

/**
 * Persisted column-visibility state for a table, keyed by `storageKey` so each table keeps its own
 * view (pass a distinct key per table). Pairs with `<ColumnSelectorDropdown />`.
 *
 * Stored as a comma-joined string (a primitive), not an array: useLocalStorage re-reads on every
 * render, and a primitive lets its internal setState bail via Object.is instead of looping on a
 * freshly-parsed array reference.
 */
export function useColumnVisibility(storageKey: string) {
  const [raw, setRaw] = useLocalStorage<string>(storageKey, "");

  const hiddenKeys = useMemo(() => parseHiddenKeys(raw), [raw]);

  const toggle = useCallback(
    (key: string) =>
      setRaw((prev) => {
        const keys = parseHiddenKeys(prev);
        const next = keys.includes(key)
          ? keys.filter((k) => k !== key)
          : [...keys, key];
        return next.join(",");
      }),
    [setRaw],
  );

  const reset = useCallback(() => setRaw(""), [setRaw]);

  return { hiddenKeys, toggle, reset };
}
